import { useState } from "react";
import {
  Box,
  HStack,
  VStack,
  Progress,
  Text,
  Button,
  Badge,
  Select,
  useColorModeValue,
  Tooltip,
  Spinner,
} from "@chakra-ui/react";

// Placing workflow steps based on the actual placing process
const SUBMISSION_STEPS = [
  { code: "submission", name: "Submission", initiator: "Broker" },
  { code: "request_additional_information", name: "Request Additional Info", initiator: "Carrier" },
  { code: "supply_additional_information", name: "Supply Additional Info", initiator: "Broker" },
  { code: "decline_to_participate", name: "Decline to Participate", initiator: "Carrier" },
];

const QUOTATION_STEPS = [
  { code: "quotation_request", name: "Quotation Request", initiator: "Broker" },
  { code: "quotation", name: "Quotation", initiator: "Carrier" },
  { code: "request_additional_information", name: "Request Additional Info", initiator: "Carrier" },
  { code: "supply_additional_information", name: "Supply Additional Info", initiator: "Broker" },
  { code: "placing_cancelledntu", name: "Placing Cancelled-NTU", initiator: "Broker" },
  { code: "decline_to_quote", name: "Decline to Quote", initiator: "Carrier" },
];

const ORDER_STEPS = [
  { code: "request_for_line", name: "Request for Line", initiator: "Broker" },
  { code: "request_additional_information", name: "Request Additional Info", initiator: "Carrier" },
  { code: "supply_additional_information", name: "Supply Additional Info", initiator: "Broker" },
  { code: "conditional_line", name: "Conditional Offer (Line)", initiator: "Carrier" },
  { code: "unconditional_line", name: "Unconditional Offer (Line)", initiator: "Carrier" },
  { code: "placing_cancelledntu", name: "Placing Cancelled-NTU", initiator: "Broker" },
  { code: "decline_line", name: "Decline Line", initiator: "Carrier" },
];

const BOUND_STEPS = [
  { code: "signed_line_advice", name: "Signed Line Advice", initiator: "Broker" },
  { code: "acceptance_of_conditions", name: "Acceptance of Conditions", initiator: "Broker" },
  { code: "decline_conditions", name: "Decline Conditions", initiator: "Broker" },
];

export default function PlacingProcessBar({
  currentStage = "submission", // "submission", "quotation", "order", or "bound"
  currentStepCode = "submission",
  participantRole = "Broker", // "Broker", "Lead Carrier", "Follower"
  onStepChange,
  onStageChange,
  placement = null, // The placement object if one exists
  isLoadingPlacement = false, // Whether we're loading placement data
  isCreatingPlacement = false, // Whether we're creating a new placement
  isDeletingPlacement = false, // Whether we're deleting a placement
  onStartPlacement, // Callback to create a new placement
  onDeletePlacement, // Callback to delete the current placement
}) {
  const bg = useColorModeValue("white", "gray.800");
  const [stage, setStage] = useState(currentStage);
  const [stepCode, setStepCode] = useState(currentStepCode);

  // If no placement exists, show the "Start Placement" state
  const hasPlacement = !!placement;

  // Get steps based on current stage
  const getStepsForStage = (stageName) => {
    switch (stageName) {
      case "submission":
        return SUBMISSION_STEPS;
      case "quotation":
        return QUOTATION_STEPS;
      case "order":
        return ORDER_STEPS;
      case "bound":
        return BOUND_STEPS;
      default:
        return SUBMISSION_STEPS;
    }
  };

  const steps = getStepsForStage(stage);
  const currentStepIndex = steps.findIndex(s => s.code === stepCode);
  const currentStep = steps[currentStepIndex] || steps[0];
  const progressPercent = steps.length > 0 ? ((currentStepIndex + 1) / steps.length) * 100 : 0;

  const handleStageChange = (newStage) => {
    setStage(newStage);
    // Reset to first step of new stage
    const newSteps = getStepsForStage(newStage);
    setStepCode(newSteps[0].code);
    if (onStageChange) {
      onStageChange(newStage, newSteps[0].code);
    }
  };

  const handleStepChange = (newStepCode) => {
    setStepCode(newStepCode);
    if (onStepChange) {
      onStepChange(stage, newStepCode);
    }
  };

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      handleStepChange(steps[currentStepIndex + 1].code);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      handleStepChange(steps[currentStepIndex - 1].code);
    }
  };

  const getStageColor = () => {
    switch (stage) {
      case "submission":
        return "blue";
      case "quotation":
        return "cyan";
      case "order":
        return "teal";
      case "bound":
        return "green";
      default:
        return "blue";
    }
  };

  const getStageDisplayName = () => {
    switch (stage) {
      case "submission":
        return "Submission";
      case "quotation":
        return "Quotation";
      case "order":
        return "Order";
      case "bound":
        return "Bound";
      default:
        return "Submission";
    }
  };

  const getParticipantColor = () => {
    if (participantRole === "Broker") return "blue";
    if (participantRole === "Lead Carrier") return "teal";
    return "cyan";
  };

  // If loading placement data, show loading state
  if (isLoadingPlacement) {
    return (
      <Box
        bg={bg}
        borderBottomWidth="1px"
        borderColor="gray.300"
        px={3}
        py={1}
      >
        <HStack spacing={2} justify="center" align="center">
          <Spinner size="xs" />
          <Text fontSize="xs" color="gray.500">Loading placement...</Text>
        </HStack>
      </Box>
    );
  }

  // If no placement exists, show Start Placement button
  if (!hasPlacement) {
    return (
      <Box
        bg={bg}
        borderBottomWidth="1px"
        borderColor="gray.300"
        px={3}
        py={1}
      >
        <HStack spacing={3} justify="flex-start" align="center">
          <Button
            size="xs"
            colorScheme="blue"
            onClick={onStartPlacement}
            isLoading={isCreatingPlacement}
            loadingText="Starting..."
          >
            Start Placement
          </Button>
        </HStack>
      </Box>
    );
  }

  // Full placing process bar when placement exists
  return (
    <Box
      bg={bg}
      borderBottomWidth="1px"
      borderColor="gray.300"
      px={3}
      py={1}
    >
      <HStack spacing={3} justify="space-between" align="center">
        {/* Left: Stage and Role badges */}
        <HStack spacing={2}>
          <Badge colorScheme={getStageColor()} fontSize="xs" px={2} py={0.5}>
            {getStageDisplayName()}
          </Badge>
          <Badge colorScheme={getParticipantColor()} fontSize="xs" px={2} py={0.5} variant="subtle">
            {participantRole}
          </Badge>
        </HStack>

        {/* Center: Progress bar with step info */}
        <HStack flex="1" spacing={2} mx={4}>
          <Tooltip label={`Initiated by: ${currentStep.initiator}`} hasArrow>
            <Text fontSize="xs" fontWeight="medium" color="gray.700" whiteSpace="nowrap">
              {currentStep.name}
            </Text>
          </Tooltip>
          <Progress
            value={progressPercent}
            size="xs"
            colorScheme={getStageColor()}
            borderRadius="md"
            flex="1"
            minW="100px"
          />
          <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
            {currentStepIndex + 1}/{steps.length}
          </Text>
        </HStack>

        {/* Right: Controls */}
        <HStack spacing={2}>
          <Select
            size="xs"
            value={stage}
            onChange={(e) => handleStageChange(e.target.value)}
            width="90px"
            variant="outline"
            fontSize="xs"
          >
            <option value="submission">Submission</option>
            <option value="quotation">Quotation</option>
            <option value="order">Order</option>
            <option value="bound">Bound</option>
          </Select>

          <Select
            size="xs"
            value={stepCode}
            onChange={(e) => handleStepChange(e.target.value)}
            width="180px"
            fontSize="xs"
          >
            {steps.map((step) => (
              <option key={step.code} value={step.code}>
                {step.name}
              </option>
            ))}
          </Select>

          <Button
            size="xs"
            variant="ghost"
            onClick={handlePrevious}
            isDisabled={currentStepIndex <= 0}
            px={2}
          >
            ←
          </Button>
          <Button
            size="xs"
            variant="ghost"
            onClick={handleNext}
            isDisabled={currentStepIndex >= steps.length - 1}
            px={2}
          >
            →
          </Button>
          <Tooltip label="Delete Placement" hasArrow>
            <Button
              size="xs"
              variant="ghost"
              colorScheme="red"
              onClick={onDeletePlacement}
              isLoading={isDeletingPlacement}
              px={2}
            >
              ✕
            </Button>
          </Tooltip>
        </HStack>
      </HStack>
    </Box>
  );
}
